import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { tApi, errText } from "@/lib/tournaments";

const EMPTY = { team_name: "", player1: "", player2: "", phone: "", email: "", skill_level: "", comments: "", accept_regulations: false, accept_privacy: false };

export default function RegistrationDialog({ open, onOpenChange, tournament, full, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_regulations || !form.accept_privacy) return toast.error("Απαιτείται αποδοχή κανονισμών και πολιτικής απορρήτου");
    setLoading(true);
    try {
      const { data } = await tApi.post(`/tournaments/${tournament.slug}/register`, { ...form, skill_level: form.skill_level || null, comments: form.comments || null });
      setDone(data.status);
      onDone?.();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => { onOpenChange(v); if (!v) { setDone(null); setForm(EMPTY); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-3xl max-w-lg max-h-[92vh] overflow-y-auto" data-testid="registration-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-widest">ΔΗΛΩΣΗ ΣΥΜΜΕΤΟΧΗΣ</DialogTitle>
          <DialogDescription>{tournament.title}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-8 space-y-3" data-testid="registration-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <p className="font-bold text-slate-900">{done === "waitlist" ? "Η ομάδα σας μπήκε στη λίστα αναμονής." : "Η δήλωσή σας καταχωρήθηκε!"}</p>
            <p className="text-sm text-slate-600">{done === "waitlist" ? "Θα ενημερωθείτε με email αν ανοίξει θέση." : "Βρίσκεται σε αναμονή έγκρισης. Θα λάβετε email επιβεβαίωσης μόλις εγκριθεί."}</p>
            <Button onClick={() => close(false)} className="rounded-full bg-ocean" data-testid="registration-close">Κλείσιμο</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {full && <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs font-bold text-amber-800">ΟΙ ΘΕΣΕΙΣ ΕΧΟΥΝ ΣΥΜΠΛΗΡΩΘΕΙ — η δήλωση θα μπει σε λίστα αναμονής.</div>}
            <Field label="Όνομα ομάδας *"><Input required value={form.team_name} onChange={set("team_name")} data-testid="reg-team-name" className="rounded-xl" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Αθλήτρια 1 — Ονοματεπώνυμο *"><Input required value={form.player1} onChange={set("player1")} data-testid="reg-player1" className="rounded-xl" /></Field>
              <Field label="Αθλήτρια 2 — Ονοματεπώνυμο *"><Input required value={form.player2} onChange={set("player2")} data-testid="reg-player2" className="rounded-xl" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Τηλέφωνο επικοινωνίας *"><Input required type="tel" value={form.phone} onChange={set("phone")} data-testid="reg-phone" className="rounded-xl" /></Field>
              <Field label="Email *"><Input required type="email" value={form.email} onChange={set("email")} data-testid="reg-email" className="rounded-xl" /></Field>
            </div>
            <Field label="Επίπεδο (προαιρετικό)">
              <select value={form.skill_level} onChange={set("skill_level")} data-testid="reg-level" className="w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                <option value="">—</option><option value="Αρχάριο">Αρχάριο</option><option value="Μεσαίο">Μεσαίο</option><option value="Προχωρημένο">Προχωρημένο</option><option value="Αγωνιστικό">Αγωνιστικό</option>
              </select>
            </Field>
            <Field label="Σχόλια (προαιρετικό)"><Textarea value={form.comments} onChange={set("comments")} data-testid="reg-comments" className="rounded-xl" rows={3} /></Field>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <Checkbox checked={form.accept_regulations} onCheckedChange={set("accept_regulations")} data-testid="reg-accept-rules" className="mt-0.5" />
              <span>Αποδέχομαι τους κανονισμούς της διοργάνωσης.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <Checkbox checked={form.accept_privacy} onCheckedChange={set("accept_privacy")} data-testid="reg-accept-privacy" className="mt-0.5" />
              <span>Συναινώ στην επεξεργασία των στοιχείων μου αποκλειστικά για τη διοργάνωση του τουρνουά (GDPR). Τα στοιχεία επικοινωνίας δεν δημοσιεύονται· δημοσιεύονται μόνο το όνομα ομάδας και τα ονόματα των αθλητριών.</span>
            </label>
            <Button type="submit" disabled={loading} data-testid="reg-submit" className="w-full h-12 rounded-full bg-sunset hover:bg-sunset-hover font-black tracking-widest">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "ΑΠΟΣΤΟΛΗ ΔΗΛΩΣΗΣ"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</Label>{children}</div>
);
